import { CanvasSource, Rectangle, Texture } from 'pixi.js';
import { Sketch } from './sketch';
import type { DoodleFn } from './doodles';
import { FX_DOODLES, HERO_DOODLES, MONSTER_DOODLES, MOVE_DOODLES } from './doodles';
import { ASSET_CATALOG, type AssetManifest } from '../../assets/catalog';

type Drawable = ImageBitmap | HTMLImageElement;

export const BOIL_FRAMES = 3;

/** Draws one frame into a cell of size w×h (origin at the cell's top-left). */
type Painter = (ctx: CanvasRenderingContext2D, w: number, h: number) => void;

interface PackItem {
  id: string;
  state: string;
  w: number;
  h: number;
  paint: Painter;
}

interface FrameRect {
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface AssetProblem {
  id: string;
  problem: string;
}

const PLACEHOLDER_SIZE: Record<string, number> = { hero: 256, monster: 256, move: 128 };
const PROCEDURAL: Record<string, Record<string, DoodleFn>> = { hero: HERO_DOODLES, monster: MONSTER_DOODLES, move: MOVE_DOODLES, fx: FX_DOODLES };

/**
 * Asset pipeline. Real art listed in /assets/manifest.json is loaded and packed
 * at startup into as few atlas pages as the GPU allows (sprites batch into few
 * draw calls). Anything the manifest does not provide yet falls back to a
 * procedural doodle and is reported in `placeholders` — the `assets:check`
 * gate refuses a release while that list is not empty.
 * Player drawings get their own small textures, reference-counted and
 * destroyed when no scene uses them any more.
 */
export class AssetCache {
  private pages: HTMLCanvasElement[] = [];
  private sources: CanvasSource[] = [];
  private states = new Map<string, Map<string, Texture[]>>();
  private rects = new Map<string, FrameRect[]>();
  private dynamic = new Map<string, { textures: Texture[]; refs: number }>();
  private images = new Map<string, Map<string, Drawable[]>>();
  private anchors = new Map<string, Map<string, [number, number]>>();
  private manifest: AssetManifest | null = null;
  private base = '/assets/';
  /** Ids still drawn by the procedural placeholder generator. */
  readonly placeholders: string[] = [];
  /** Manifest entries that failed to load or did not match the catalog. */
  readonly problems: AssetProblem[] = [];
  manifestFound = false;

  /** Load real art listed in the manifest (missing manifest = everything is a placeholder). */
  async loadManifest(url: string, onProgress: (done: number, total: number) => void = () => {}): Promise<void> {
    let manifest: AssetManifest | null = null;
    try {
      const res = await fetch(url, { cache: 'no-cache' });
      if (res.ok) manifest = (await res.json()) as AssetManifest;
    } catch {
      manifest = null;
    }
    this.manifestFound = manifest !== null;
    this.manifest = manifest;
    if (!manifest) return;
    const base = url.slice(0, url.lastIndexOf('/') + 1);
    // Absolute, so DOM art used through CSS variables resolves the same under any base path.
    this.base = typeof document === 'undefined' ? base : new URL(base, document.baseURI).href;
    const pixiIds = new Set(ASSET_CATALOG.filter((s) => s.target === 'pixi').map((s) => s.id));
    let total = 0;
    for (const [id, e] of Object.entries(manifest.assets)) if (pixiIds.has(id)) for (const f of Object.values(e.states)) total += f.length;
    let done = 0;
    const tick = <T>(p: Promise<T>): Promise<T> =>
      p.finally(() => {
        onProgress(++done, total);
      });
    onProgress(0, total);
    await Promise.all(
      ASSET_CATALOG.filter((s) => s.target === 'pixi').map(async (spec) => {
        const entry = manifest.assets[spec.id];
        if (!entry) return;
        const byState = new Map<string, Drawable[]>();
        const anchors = new Map<string, [number, number]>();
        for (const [state, files] of Object.entries(entry.states)) {
          // Prefer the WebP runtime copy of a PNG master when one was exported.
          const resolved = files.map((f) => manifest.runtime?.[f]?.file ?? f);
          const loaded = await Promise.allSettled(resolved.map((f) => tick<Drawable>(f.endsWith('.svg') ? loadSvg(base + f) : loadBitmap(base + f))));
          const ok: Drawable[] = [];
          loaded.forEach((r, i) => {
            if (r.status === 'fulfilled') ok.push(r.value);
            else this.problems.push({ id: spec.id, problem: `${files[i]} failed to load` });
          });
          if (ok.length) byState.set(state, ok);
          anchors.set(state, entry.anchors?.[state] ?? entry.anchor ?? spec.anchor);
        }
        this.anchors.set(spec.id, anchors);
        if (byState.has('idle')) this.images.set(spec.id, byState);
        else this.problems.push({ id: spec.id, problem: 'no usable idle frames' });
      }),
    );
  }

  /** Pack real art (or placeholders) into atlas pages. `maxSize` comes from the GPU limits. */
  buildAtlas(maxSize: number): void {
    // 2048² pages (16 MB each) stay friendly to phone GPUs; characters are
    // stored at 384px, enough for their largest on-screen size at 2× DPR.
    const pageSize = Math.min(2048, maxSize);
    const charCell = 384;
    const items: PackItem[] = [];
    for (const spec of ASSET_CATALOG) {
      if (spec.target !== 'pixi') continue;
      const real = this.images.get(spec.id);
      if (real) {
        // One scale per asset (from the reference size), applied to each file's
        // real dimensions, so wide poses keep the character's pixel scale.
        const scale = spec.group === 'characters' ? charCell / spec.size[1] : spec.format === 'svg' ? 2 : 1;
        for (const [state, images] of real)
          for (const img of images) {
            const w = Math.round(img.width * scale);
            const h = Math.round(img.height * scale);
            items.push({ id: spec.id, state, w, h, paint: (ctx, cw, ch) => ctx.drawImage(img, 0, 0, cw, ch) });
          }
        continue;
      }
      // Placeholder: procedural doodle, idle state only.
      const [kind, name] = spec.id.split(':') as [string, string];
      const draw = PROCEDURAL[kind]?.[name];
      if (!draw) continue;
      this.placeholders.push(spec.id);
      const size = PLACEHOLDER_SIZE[kind] ?? (name === 'impact' || name === 'pow' ? 160 : 64);
      const frames = kind === 'fx' ? Math.min(2, spec.states.idle ?? 1) : (spec.states.idle ?? 1);
      for (let f = 0; f < frames; f++) {
        const seed = 1337 + hash(spec.id) + f * 7919;
        items.push({ id: spec.id, state: 'idle', w: size, h: size, paint: (ctx, cw) => draw(new Sketch(ctx, seed), cw) });
      }
    }

    const packed = shelfPack(items, pageSize);
    this.pages = packed.pageHeights.map((ph) => {
      const c = document.createElement('canvas');
      c.width = pageSize;
      c.height = Math.min(nextPow2(ph), pageSize);
      return c;
    });
    const ctxs = this.pages.map((c) => {
      const ctx = c.getContext('2d');
      if (!ctx) throw new Error('2D canvas unavailable');
      return ctx;
    });
    items.forEach((item, i) => {
      const r = packed.rects[i]!;
      const ctx = ctxs[r.page]!;
      ctx.save();
      ctx.beginPath();
      ctx.rect(r.x, r.y, r.w, r.h);
      ctx.clip();
      ctx.translate(r.x, r.y);
      item.paint(ctx, r.w, r.h);
      ctx.restore();
      const key = `${item.id}|${item.state}`;
      const list = this.rects.get(key) ?? [];
      list.push(r);
      this.rects.set(key, list);
    });
    // Source images are now in the atlas; free their memory.
    for (const states of this.images.values()) for (const list of states.values()) for (const b of list) if ('close' in b) b.close();
    this.images.clear();

    this.sources = this.pages.map((c, i) => new CanvasSource({ resource: c, label: `atlas-${i}` }));
    for (const [key, rects] of this.rects) {
      const [id, state] = key.split('|') as [string, string];
      const textures = rects.map((r) => new Texture({ source: this.sources[r.page]!, frame: new Rectangle(r.x, r.y, r.w, r.h), label: `${id}:${state}` }));
      let byState = this.states.get(id);
      if (!byState) this.states.set(id, (byState = new Map()));
      byState.set(state, textures);
    }
    if (this.placeholders.length) console.warn(`[assets] ${this.placeholders.length} placeholder(s) in use — not releasable:`, this.placeholders.join(', '));
    for (const p of this.problems) console.warn(`[assets] ${p.id}: ${p.problem}`);
  }

  /** Idle frames for an asset, e.g. "hero:pencil_knight". */
  get(id: string): Texture[] {
    const t = this.states.get(id)?.get('idle');
    if (!t) throw new Error(`asset ${id} not in atlas`);
    return t;
  }

  /** Frames for an animation state; falls back to idle when the art has no such state. */
  getState(id: string, state: string): Texture[] {
    return this.states.get(id)?.get(state) ?? this.get(id);
  }

  has(id: string): boolean {
    return this.states.has(id);
  }

  /** Pivot of a state's frames (normalised); placeholders use the catalog default. */
  anchorOf(id: string, state = 'idle'): [number, number] {
    return this.anchors.get(id)?.get(state) ?? this.anchors.get(id)?.get('idle') ?? ASSET_CATALOG.find((s) => s.id === id)?.anchor ?? [0.5, 0.5];
  }

  /** URL of a DOM asset frame from the manifest (its WebP runtime copy when exported), or null when not delivered yet. */
  url(id: string, state = 'idle', index = 0): string | null {
    const f = this.manifest?.assets[id]?.states[state]?.[index];
    return f ? this.base + (this.manifest?.runtime?.[f]?.file ?? f) : null;
  }

  /** True when the asset is real art (not a procedural placeholder). */
  isReal(id: string): boolean {
    return this.has(id) && !this.placeholders.includes(id);
  }

  /** Copy an atlas frame into a DOM canvas (thumbnails in the notebook UI). */
  drawThumb(target: HTMLCanvasElement, id: string, frame = 0): void {
    const rect = this.rects.get(`${id}|idle`)?.[frame];
    const ctx = target.getContext('2d');
    const page = rect ? this.pages[rect.page] : undefined;
    if (!rect || !ctx || !page) return;
    ctx.clearRect(0, 0, target.width, target.height);
    const s = Math.min(target.width / rect.w, target.height / rect.h);
    const w = rect.w * s;
    const hh = rect.h * s;
    ctx.drawImage(page, rect.x, rect.y, rect.w, rect.h, (target.width - w) / 2, (target.height - hh) / 2, w, hh);
  }

  /**
   * Register (or re-use) textures generated at runtime, e.g. player drawings.
   * Every acquire must be paired with release().
   */
  acquireDynamic(key: string, make: () => HTMLCanvasElement[]): Texture[] {
    const existing = this.dynamic.get(key);
    if (existing) {
      existing.refs++;
      return existing.textures;
    }
    const textures = make().map((c) => new Texture({ source: new CanvasSource({ resource: c }), label: key }));
    this.dynamic.set(key, { textures, refs: 1 });
    return textures;
  }

  release(key: string): void {
    const entry = this.dynamic.get(key);
    if (!entry) return;
    entry.refs--;
    if (entry.refs <= 0) {
      for (const t of entry.textures) t.destroy(true);
      this.dynamic.delete(key);
    }
  }

  get stats(): { atlasTextures: number; dynamicTextures: number; placeholders: number; problems: number } {
    let dyn = 0;
    for (const e of this.dynamic.values()) dyn += e.textures.length;
    return { atlasTextures: this.sources.length, dynamicTextures: dyn, placeholders: this.placeholders.length, problems: this.problems.length };
  }
}

function nextPow2(v: number): number {
  let p = 1;
  while (p < v) p *= 2;
  return p;
}

/** Shelf packer over multiple pages; tallest items first. Returns rects in input order. */
function shelfPack(items: PackItem[], pageSize: number): { rects: FrameRect[]; pageHeights: number[] } {
  const pad = 2;
  const order = items.map((_, i) => i).sort((a, b) => items[b]!.h - items[a]!.h);
  const rects: FrameRect[] = new Array(items.length);
  const pageHeights: number[] = [0];
  let page = 0;
  let x = 0;
  let y = 0;
  let shelf = 0;
  for (const i of order) {
    const it = items[i]!;
    if (x + it.w > pageSize) {
      x = 0;
      y += shelf + pad;
      shelf = 0;
    }
    if (y + it.h > pageSize) {
      page++;
      pageHeights.push(0);
      x = 0;
      y = 0;
      shelf = 0;
    }
    rects[i] = { page, x, y, w: it.w, h: it.h };
    x += it.w + pad;
    shelf = Math.max(shelf, it.h);
    pageHeights[page] = Math.max(pageHeights[page]!, y + it.h);
  }
  return { rects, pageHeights };
}

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0;
  return h & 0xffff;
}

/** SVGs go through an <img> (createImageBitmap does not accept SVG blobs everywhere). */
async function loadSvg(url: string): Promise<HTMLImageElement> {
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
  await img.decode();
  return img;
}

async function loadBitmap(url: string): Promise<ImageBitmap> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return createImageBitmap(await res.blob(), { premultiplyAlpha: 'premultiply' });
}
